import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../lib/auth";
import { readImpersonationClaim, stopImpersonation } from "../lib/impersonate";
import { getSupabase } from "../lib/supabase";

/**
 * MER-197: yellow banner pinned to the top of the viewport whenever the
 * authenticated session carries an impersonation claim. Renders the
 * impersonated tenant's display name plus an Exit button that stops
 * impersonation and routes back to /admin/customers.
 *
 * Returns null when not impersonating so it costs nothing on the
 * customer-facing path.
 */
export default function ImpersonationBanner() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const claim = readImpersonationClaim(session);
  const tenantId = claim?.viewAsTenantId ?? null;
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setTenantName(null);
      return;
    }
    let cancelled = false;
    setTenantName(null);
    // While impersonating, RLS scopes `tenants` to the view-as row, so
    // this query reliably returns exactly the impersonated tenant's
    // name even though we're querying as the staff user.
    (async () => {
      try {
        const { data } = await getSupabase()
          .from("tenants")
          .select("name")
          .eq("id", tenantId)
          .maybeSingle();
        if (cancelled) return;
        const name = (data as { name?: string } | null)?.name ?? null;
        setTenantName(name);
      } catch {
        if (cancelled) return;
        setTenantName(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (!claim) return null;

  async function handleExit() {
    setBusy(true);
    setError(null);
    try {
      await stopImpersonation();
      navigate("/admin/customers");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        background: "var(--flag-yellow, #F2C94C)",
        color: "#1A1A1A",
        padding: "10px 20px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        fontSize: 13,
        fontWeight: 600,
        borderBottom: "1px solid rgba(0,0,0,0.12)",
      }}
    >
      <span style={{ flex: 1 }}>
        Viewing as <strong>{tenantName ?? "customer"}</strong>. All data is scoped to this tenant.
        {error ? (
          <span style={{ marginLeft: 12, color: "var(--flag-red, #C0392B)", fontWeight: 700 }}>
            {error}
          </span>
        ) : null}
      </span>
      <button
        type="button"
        onClick={handleExit}
        disabled={busy}
        style={{
          font: "inherit",
          fontSize: 12,
          fontWeight: 700,
          padding: "6px 14px",
          borderRadius: 6,
          border: "1px solid rgba(0,0,0,0.2)",
          background: "#1A1A1A",
          color: "#F2C94C",
          cursor: busy ? "wait" : "pointer",
        }}
      >
        {busy ? "Exiting..." : "Exit view-as"}
      </button>
    </div>
  );
}
